import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Modal from '../components/common/Modal';
import { Button } from '../components/common/Button';
import SearchableSelect from '../components/common/SearchableSelect';
import { missingItemService } from '../services/missingItemService';
import { MissingItem, MISSING_ITEM_CATEGORIES, MISSING_ITEM_PRIORITIES, MISSING_ITEM_STATUSES } from '../types/missingItem';
import { useCustomers } from '../hooks/useCustomers';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

const MissingItems = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const [items, setItems] = useState<MissingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MissingItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MissingItem | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    customerId: '',
    description: '',
    category: 'document' as const,
    priority: 'medium' as const,
    dueDate: '',
    notes: ''
  });

  useEffect(() => {
    if (user?.organizationId) {
      loadMissingItems();
    }
  }, [user]);

  const loadMissingItems = async () => {
    if (!user?.organizationId) return;
    
    try {
      setLoading(true);
      const fetchedItems = await missingItemService.getMissingItemsByOrganization(user.organizationId);
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error loading missing items:', error);
      toast.error('Hiba a hiánylista betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.organizationId || !user?.id) return;

    // Validáció
    if (!formData.customerId) {
      toast.error('Kérlek válassz ki egy ügyfelet!');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Kérlek add meg a leírást!');
      return;
    }

    try {
      await missingItemService.createMissingItem({
        customerId: formData.customerId,
        organizationId: user.organizationId,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        notes: formData.notes,
        status: 'pending',
        createdBy: user.id
      });

      toast.success('Hiányzó tétel létrehozva');
      setShowCreateModal(false);
      resetForm();
      loadMissingItems();
    } catch (error) {
      console.error('Error creating missing item:', error);
      toast.error('Hiba a létrehozás során');
    }
  };

  const handleApprove = async (itemId: string) => {
    if (!user?.id) return;
    
    try {
      await missingItemService.approveMissingItem(itemId, user.id);
      toast.success('Tétel jóváhagyva');
      loadMissingItems();
    } catch (error) {
      console.error('Error approving item:', error);
      toast.error('Hiba a jóváhagyás során');
    }
  };

  const handleEditClick = (item: MissingItem) => {
    setEditingItem(item);
    setFormData({
      customerId: item.customerId,
      description: item.description,
      category: item.category as any,
      priority: item.priority as any,
      dueDate: item.dueDate ? item.dueDate.toISOString().split('T')[0] : '',
      notes: item.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !editingItem) return;

    // Validáció
    if (!formData.customerId) {
      toast.error('Kérlek válassz ki egy ügyfelet!');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Kérlek add meg a leírást!');
      return;
    }

    try {
      await missingItemService.updateMissingItem(editingItem.id, {
        customerId: formData.customerId,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        notes: formData.notes
      });

      toast.success('Tétel módosítva');
      setShowEditModal(false);
      setEditingItem(null);
      resetForm();
      loadMissingItems();
    } catch (error) {
      console.error('Error updating missing item:', error);
      toast.error('Hiba a módosítás során');
    }
  };

  const handleDeleteClick = (item: MissingItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      await missingItemService.deleteMissingItem(itemToDelete.id);
      toast.success('Tétel törölve');
      loadMissingItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Hiba a törlés során');
    } finally {
      setItemToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      description: '',
      category: 'document',
      priority: 'medium',
      dueDate: '',
      notes: ''
    });
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return 'Ismeretlen ügyfél';
    
    if (customer.type === 'organization') {
      return customer.companyName;
    }
    return `${customer.lastName} ${customer.firstName}`;
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = getCustomerName(item.customerId).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || item.priority === filterPriority;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444'; // red-500
      case 'high': return '#f97316'; // orange-500
      case 'medium': return '#eab308'; // yellow-500
      case 'low': return '#22c55e'; // green-500
      default: return '#6b7280'; // gray-500
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#94a3b8'; // slate-400
      case 'acknowledged': return '#2563eb'; // blue-600
      case 'uploaded': return '#7c3aed'; // purple-600
      case 'approved': return '#16a34a'; // green-600
      case 'rejected': return '#dc2626'; // red-600
      default: return '#6b7280'; // gray-500
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300 border-slate-200 dark:border-slate-800';
      case 'acknowledged': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'uploaded': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800';
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const stats = {
    total: items.length,
    pending: items.filter(i => i.status === 'pending').length,
    acknowledged: items.filter(i => i.status === 'acknowledged').length,
    uploaded: items.filter(i => i.status === 'uploaded').length,
    approved: items.filter(i => i.status === 'approved').length
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-6">
      <CommonPageHeader
        title="Hiánylista kezelés"
        subtitle="Hiányzó dokumentumok és bizonylatok nyilvántartása"
        icon={ExclamationTriangleIcon}
      />

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Összes</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Függőben</p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Nyugtázva</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.acknowledged}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Feltöltve</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.uploaded}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Jóváhagyva</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.approved}</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés ügyfél vagy leírás alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="all">Minden státusz</option>
            {Object.entries(MISSING_ITEM_STATUSES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Priority Filter */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="all">Minden prioritás</option>
            {Object.entries(MISSING_ITEM_PRIORITIES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Create Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center space-x-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Új tétel</span>
          </button>
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Nincs találat
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Próbálj meg más keresési feltételeket
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all border-l-4 border-r-4"
                style={{
                  borderLeftColor: getStatusColor(item.status),
                  borderRightColor: getPriorityColor(item.priority)
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                        {getCustomerName(item.customerId)}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityBadgeClass(item.priority)}`}>
                        {MISSING_ITEM_PRIORITIES[item.priority]}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(item.status)}`}>
                        {MISSING_ITEM_STATUSES[item.status]}
                      </span>
                      <span className="px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                        {MISSING_ITEM_CATEGORIES[item.category]}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      {item.description}
                    </p>
                    
                    {item.notes && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Megjegyzés: {item.notes}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>Létrehozva: {item.createdAt.toLocaleDateString('hu-HU')}</span>
                      {item.dueDate && (
                        <span className="text-red-600 dark:text-red-400">
                          Határidő: {item.dueDate.toLocaleDateString('hu-HU')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-4 flex flex-col space-y-2">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center space-x-1"
                        title="Szerkesztés"
                      >
                        <PencilSquareIcon className="h-3 w-3" />
                        <span>Szerkesztés</span>
                      </button>
                      {item.status === 'uploaded' && (
                        <button
                          onClick={() => handleApprove(item.id)}
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                        >
                          Jóváhagyás
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteClick(item)}
                      className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 w-full"
                    >
                      Törlés
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Új hiányzó tétel létrehozása"
        size="2xl"
      >
        <form onSubmit={handleCreate} className="p-6 space-y-4">
          {/* Alapadatok Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ExclamationTriangleIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Alapadatok
                </h3>

                {/* Customer Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ügyfél <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    options={customers.map(customer => ({
                      value: customer.id,
                      label: customer.type === 'organization' 
                        ? customer.companyName 
                        : `${customer.lastName} ${customer.firstName}`
                    }))}
                    value={formData.customerId}
                    onChange={(value) => setFormData({ ...formData, customerId: value })}
                    placeholder="Válassz ügyfelet..."
                    searchPlaceholder="Keresés ügyfelek között..."
                    clearable={true}
                  />
                  {!formData.customerId && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Kötelező mező
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="pl. utolsó havi bankszámlakivonat"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategória
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {Object.entries(MISSING_ITEM_CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Részletek Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Részletek
                </h3>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Prioritás
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {Object.entries(MISSING_ITEM_PRIORITIES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Határidő
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Megjegyzés
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                    placeholder="További információk..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Létrehozás
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingItem(null);
          resetForm();
        }}
        title="Hiányzó tétel szerkesztése"
        size="2xl"
      >
        <form onSubmit={handleUpdate} className="p-6 space-y-4">
          {/* Alapadatok Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ExclamationTriangleIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Alapadatok
                </h3>

                {/* Customer Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ügyfél <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    options={customers.map(customer => ({
                      value: customer.id,
                      label: customer.type === 'organization' 
                        ? customer.companyName 
                        : `${customer.lastName} ${customer.firstName}`
                    }))}
                    value={formData.customerId}
                    onChange={(value) => setFormData({ ...formData, customerId: value })}
                    placeholder="Válassz ügyfelet..."
                    searchPlaceholder="Keresés ügyfelek között..."
                    clearable={true}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="pl. utolsó havi bankszámlakivonat"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategória
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {Object.entries(MISSING_ITEM_CATEGORIES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Részletek Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Részletek
                </h3>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Prioritás
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {Object.entries(MISSING_ITEM_PRIORITIES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Határidő
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Megjegyzés
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                    placeholder="További információk..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingItem(null);
                resetForm();
              }}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Mentés
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Tétel törlése"
        message="Biztosan törölni szeretnéd ezt a hiányzó tételt? Ez a művelet nem vonható vissza."
        itemName={itemToDelete ? `${getCustomerName(itemToDelete.customerId)} - ${itemToDelete.description}` : ''}
        confirmText="Törlés"
        cancelText="Mégse"
        type="danger"
      />
    </div>
  );
};

export default MissingItems;
